<template>
  <div class="nav-shell">
    <!-- Icon Rail -->
    <nav class="nav-rail" aria-label="Main navigation">
      <a href="/" class="nav-rail__logo" aria-label="Home">
        <img :src="logoSrc" alt="What's My Theology" class="nav-rail__logo-img" />
      </a>

      <!-- Direct-nav items (e.g. Login when logged out) -->
      <a
        v-for="item in directItems"
        :key="item.key"
        :href="item.href || '#'"
        class="nav-rail__item"
        :class="{ 'nav-rail__item--active': isDirectActive(item) }"
        :aria-label="item.label"
      >
        <div v-if="item.image" class="nav-rail__avatar">
          <div class="uploaded-img" :style="{ backgroundImage: `url(${item.image})` }"></div>
        </div>
        <div
          v-else-if="item.mono"
          class="nav-rail__avatar nav-rail__avatar--mono"
          :style="{ background: item.mono.background }"
        >{{ item.mono.initials }}</div>
        <i v-else :class="['fas', item.icon]"></i>
        <span v-if="item.badge && item.badge > 0" class="nav-rail__badge">{{ item.badge }}</span>
        <span class="nav-rail__tooltip">{{ item.label }}</span>
      </a>

      <!-- User panel items (Profile, Social) -->
      <button
        v-for="item in userPanelItems"
        :key="item.key"
        class="nav-rail__item"
        :class="{ 'nav-rail__item--active': activePanel === item.key }"
        :aria-label="item.label"
        @click="setActivePanel(item.key)"
      >
        <div v-if="item.image" class="nav-rail__avatar">
          <div class="uploaded-img" :style="{ backgroundImage: `url(${item.image})` }"></div>
        </div>
        <div
          v-else-if="item.mono"
          class="nav-rail__avatar nav-rail__avatar--mono"
          :style="{ background: item.mono.background }"
        >{{ item.mono.initials }}</div>
        <i v-else :class="['fas', item.icon]"></i>
        <span v-if="item.badge && item.badge > 0" class="nav-rail__badge">{{ item.badge }}</span>
        <span class="nav-rail__tooltip">{{ item.label }}</span>
      </button>

      <div v-if="userPanelItems.length || directItems.length" class="nav-rail__divider"></div>

      <!-- Content panel items (Modules, Results) -->
      <button
        v-for="item in contentPanelItems"
        :key="item.key"
        class="nav-rail__item"
        :class="{ 'nav-rail__item--active': activePanel === item.key }"
        :aria-label="item.label"
        @click="setActivePanel(item.key)"
      >
        <i :class="['fas', item.icon]"></i>
        <span class="nav-rail__tooltip">{{ item.label }}</span>
      </button>

      <div v-if="contentPanelItems.length && utilityPanelItems.length" class="nav-rail__divider"></div>

      <!-- Utility panel items (Settings) -->
      <button
        v-for="item in utilityPanelItems"
        :key="item.key"
        class="nav-rail__item"
        :class="{ 'nav-rail__item--active': activePanel === item.key }"
        :aria-label="item.label"
        @click="setActivePanel(item.key)"
      >
        <i :class="['fas', item.icon]"></i>
        <span class="nav-rail__tooltip">{{ item.label }}</span>
      </button>

      <div class="nav-rail__divider"></div>

      <!-- Utility links: FAQ, About -->
      <a
        v-for="fl in footerLinksTop"
        :key="fl.href"
        :href="fl.href"
        class="nav-rail__item"
        :class="{ 'nav-rail__item--active': isFooterActive(fl.href) }"
        :aria-label="fl.label"
      >
        <i :class="['fas', fl.icon]"></i>
        <span class="nav-rail__tooltip">{{ fl.label }}</span>
      </a>

      <!-- Spacer pushes bottom items down -->
      <div class="nav-rail__spacer"></div>

      <!-- Bottom utility: Bug Report -->
      <a
        v-for="fl in footerLinksBottom"
        :key="fl.href"
        :href="fl.href"
        class="nav-rail__item"
        :class="{ 'nav-rail__item--active': isFooterActive(fl.href) }"
        :aria-label="fl.label"
      >
        <i :class="['fas', fl.icon]"></i>
        <span class="nav-rail__tooltip">{{ fl.label }}</span>
      </a>

      <!-- Admin link at bottom -->
      <a
        v-if="localNavData.admin"
        :href="localNavData.admin.href"
        class="nav-rail__item"
        aria-label="Admin"
      >
        <i class="fas fa-wrench"></i>
        <span class="nav-rail__tooltip">Admin</span>
      </a>
    </nav>

    <!-- Panel -->
    <div class="nav-panel">
      <NavPanel
        :panel="currentPanelData"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import NavPanel from './NavPanel.vue'
import { useNavData } from '../composables/useNavData'

const props = defineProps({
  navData: Object
})

const logoSrc = new URL('../images/logo.png', import.meta.url).href
const currentPath = ref(window.location.pathname)
const { localNavData, applyNavAuthPayload } = useNavData(props.navData)

function updatePath() {
  currentPath.value = window.location.pathname
  activePanel.value = detectActivePanel()
}

// Determine initial active panel from current URL
function detectActivePanel() {
  const path = currentPath.value || window.location.pathname
  if (path === '/settings' || path.startsWith('/settings/')) return 'settings'
  if (/^\/users\/\d+\/(profile|settings)/.test(path) || /^\/u\/[^/]+\/profile/.test(path) || path === '/account' || path.startsWith('/account/')) return 'profile'
  if (/^\/users\/\d+\/(friends|groups)/.test(path) || /^\/u\/[^/]+\/friends/.test(path) || path.startsWith('/social') || path.startsWith('/feed') || path.startsWith('/groups')) return 'social'
  if (path.startsWith('/results')) return 'results'
  return 'modules'
}

const activePanel = ref(detectActivePanel())

const directItems = computed(() =>
  localNavData.value.rail_items.filter(i => i.type === 'direct')
)

const panelItems = computed(() =>
  localNavData.value.rail_items.filter(i => i.type === 'panel')
)

const userPanelItems = computed(() =>
  panelItems.value.filter(i => ['profile', 'social'].includes(i.key))
)

const contentPanelItems = computed(() =>
  panelItems.value.filter(i => ['modules', 'results'].includes(i.key))
)

const utilityPanelItems = computed(() =>
  panelItems.value.filter(i => i.key === 'settings')
)

const currentPanelData = computed(() =>
  localNavData.value.panels?.[activePanel.value] || localNavData.value.panels?.modules
)

function setActivePanel(key) {
  activePanel.value = key
}

function isDirectActive(item) {
  return item.href && (currentPath.value === item.href || currentPath.value.startsWith(item.href + '/'))
}

const PINNED_FOOTER_KEYS = ['bug_report']

const footerLinksTop = computed(() =>
  (localNavData.value.footer_links || []).filter(fl => !PINNED_FOOTER_KEYS.includes(fl.key))
)

const footerLinksBottom = computed(() =>
  (localNavData.value.footer_links || []).filter(fl => PINNED_FOOTER_KEYS.includes(fl.key))
)

function isFooterActive(href) {
  return currentPath.value === href
}

// Listen for auth updates (login/logout via Turbo)
function handleAuthUpdate(event) {
  applyNavAuthPayload(event)

  if (!localNavData.value.panels?.[activePanel.value]) {
    activePanel.value = detectActivePanel()
  }
}

function handleNameUpdate(event) {
  const profileItem = localNavData.value.rail_items.find(i => i.key === 'profile')
  if (profileItem) {
    profileItem.label = event.detail.name || 'User Profile'
  }
  if (localNavData.value.panels?.profile) {
    localNavData.value.panels.profile.title = event.detail.name || 'My Profile'
  }
}

onMounted(() => {
  document.addEventListener('turbo:load', updatePath)
  window.addEventListener('nav:auth-updated', handleAuthUpdate)
  window.addEventListener('user:name-updated', handleNameUpdate)
})

onUnmounted(() => {
  document.removeEventListener('turbo:load', updatePath)
  window.removeEventListener('nav:auth-updated', handleAuthUpdate)
  window.removeEventListener('user:name-updated', handleNameUpdate)
})
</script>

<style scoped>
.nav-shell {
  display: flex;
  height: 100vh;
  height: 100dvh; /* track iPadOS collapsing URL bar */
  overflow: hidden;
}

/* ── Icon Rail ── */
.nav-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 64px;
  min-width: 64px;
  padding: 8px 0;
  gap: 2px;
  background: var(--rail-bg);
  border-right: 1px solid var(--rail-border);
  overflow-y: auto;
}

.nav-rail__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 6px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  flex-shrink: 0;
}

.nav-rail__logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.nav-rail__item {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  border: none;
  background: none;
  color: var(--rail-text);
  cursor: pointer;
  font-size: 1.0625em; /* 17px vs body (16px) */
  transition: background 0.12s ease, color 0.12s ease;
  flex-shrink: 0;
  text-decoration: none;
}

.nav-rail__item:hover {
  background: var(--rail-item-bg-hover);
  color: var(--rail-text-strong);
}

.nav-rail__item--active {
  background: var(--rail-item-bg-active);
  color: var(--rail-text-strong);
}

.nav-rail__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
}

.nav-rail__avatar .uploaded-img {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.nav-rail__avatar--mono {
  display: grid;
  place-items: center;
  color: var(--rail-on-accent);
  font-size: 0.706em; /* 12px vs __item (17px) */
  font-weight: 700;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  user-select: none;
}

.nav-rail__badge {
  position: absolute;
  top: 1px;
  right: 1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 999px;
  background: var(--danger);
  color: var(--rail-on-accent);
  font-size: 0.529em; /* 9px vs __item (17px) */
  font-weight: 700;
  line-height: 1;
}

.nav-rail__tooltip {
  display: none;
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background: var(--rail-tooltip-bg);
  color: var(--rail-on-accent);
  font-size: 0.647em; /* 11px vs __item (17px) */
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
}

.nav-rail__item:hover .nav-rail__tooltip {
  display: block;
}

.nav-rail__divider {
  width: 20px;
  height: 1px;
  background: var(--rail-divider);
  margin: 6px 0;
  flex-shrink: 0;
}

.nav-rail__spacer {
  flex: 1;
}

/* ── Panel ── */
.nav-panel {
  width: 300px;
  min-width: 300px;
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  background: var(--rail-panel-bg);
  border-right: 1px solid var(--rail-border);
}

@media (max-width: 768px) {
  .nav-shell {
    display: none;
  }
}
</style>
